import { inject } from '@angular/core';
import { ActivatedRouteSnapshot, CanActivateFn, Navigation, Router, RouterStateSnapshot } from '@angular/router';
import { NavController } from '@ionic/angular/standalone';
import { TabService } from '../navigation/tab.service';
import { AuthFacade } from '../../facades/auth.facade';
import { AuthTokenService } from './auth-token.service';

export const authGuard: CanActivateFn = (
  _route: ActivatedRouteSnapshot,
  state: RouterStateSnapshot
): boolean => {
  const authFacade: AuthFacade = inject(AuthFacade);

  if (authFacade.isAuthenticated() || inject(AuthTokenService).hasSession()) {
    return true;
  }

  const requestedUrl: string = state.url;
  const router: Router = inject(Router);
  const navController: NavController = inject(NavController);
  const tabService: TabService = inject(TabService);

  // Detect if this is a charity or business portal route
  const isCharityRoute: boolean = requestedUrl.startsWith('/charity');
  const isBusinessRoute: boolean = requestedUrl.startsWith('/business');

  if (isCharityRoute || isBusinessRoute) {
    // Store the intended destination for charity/business portals
    sessionStorage.setItem('returnUrl', requestedUrl);

    // Redirect to login page with returnUrl query param
    router.navigate(['/login'], {
      queryParams: { returnUrl: requestedUrl }
    });
  } else {
    // User portal routes: keep existing behavior
    if (!requestedUrl.startsWith('/login') &&
        !requestedUrl.startsWith('/signup') &&
        !requestedUrl.startsWith('/start')) {
      sessionStorage.setItem('returnUrl', requestedUrl);
    }

    tabService.setLastTab('user', 'discover');
    navController.navigateRoot(['/main/discover']);
  }

  return false;
};

export const externalLinkTabGuard: CanActivateFn = async (
  route: ActivatedRouteSnapshot,
  _state: RouterStateSnapshot
): Promise<boolean> => {
  const router: Router = inject(Router);
  const tabService: TabService = inject(TabService);

  const pageToTabMappings: { [key: string]: string } = {
    'auctions': 'commerce',
    'item-detail': 'commerce',
    'charity': 'discover',
    'impact-point': 'discover',
    'fund-a-need': 'discover',
    'sunny-drive': 'discover',
  };

  const navigation: Navigation | null = router.getCurrentNavigation();
  const isExternalNavigation: boolean = !navigation?.previousNavigation;
  
  if (isExternalNavigation) {
    const urlSegments: string[] = _state.url.split('/').filter(segment => segment);
    let pageName: string = '';
    for (let i: number = 0; i < urlSegments.length; i++) {
      const segment: string = urlSegments[i].split('?')[0];
      if (pageToTabMappings[segment]) {
        pageName = segment;
        break;
      }
    }
    
    const targetTab: string = pageToTabMappings[pageName];
    if (targetTab) {
      const isUserRoute: boolean = _state.url.includes('/main');
      const isBusinessRoute: boolean = _state.url.includes('/business');
      let portal: "user" | "charity" | "business";
      
      if (isUserRoute) {
        portal = 'user';
      } else if (isBusinessRoute) {
        portal = 'business';
      } else {
        portal = 'charity';
      }
      
      tabService.setLastTab(portal, targetTab);
      tabService.trackNavigation(portal, targetTab, _state.url, {});
    }
  }
  return true;
};
